// Copyright (c) 2026 Michael F. Kamprath
//
// app.js -- top-level composition for Ewe Can Count.
//
// Phase 1a: sets up the PixiJS canvas, orientation prompt, splash
// overlay, and placeholder home screen. No game logic yet.

import { createCanvas, getLayoutVariant } from './game/canvas.js';
import { loadThemeData, buildScene } from './game/scene.js';
import { createCloudSpawner } from './game/cloud-spawner.js';
import { createBeeSpawner } from './game/bee-spawner.js';
import { createScreenFlash } from './game/screen-flash.js';
import {
  loadAnimationsData,
  runEntrance,
  preloadCycleTextures,
  collectSceneSoundUrls,
  settleEntranceSprites,
} from './game/entrance.js';
import { createIdleManager, preloadIdleTextures } from './game/idle.js';
import { createTapManager, preloadTouchReactionTextures, collectTouchReactionAssets } from './game/tap.js';
import { createLearnTapManager, collectLearnVoiceUrls } from './game/learn-tap.js';
import { createCountModel } from './game/count-model.js';
import { createThemePool } from './game/theme-pool.js';
import { createLearnModeMachine } from './game/learn-mode.js';
import { createQuizModeMachine } from './game/quiz-mode.js';
import { createQuizPlaqueManager } from './game/quiz-plaque-manager.js';
import { preloadChoicePlaqueTextures } from './game/choice-plaque.js';
import { preloadPlaqueTextures } from './game/number-plaque.js';
import { collectPlaqueAnimatorAssets, collectPlaqueReactionAssets } from './game/plaque-animators.js';
import { preloadScriptFont } from './numerals.js';
// PixiJS is statically imported by every src/game/*.js module
// (entrance, scene, idle, tap, choice-plaque, number-plaque,
// quiz-plaque-manager, cloud-spawner, bee-spawner, screen-flash,
// canvas, etc.) so it lands in the main JS chunk regardless.  We
// pull `Assets` here as a static import too -- previously this
// file used `await import('pixi.js')` inside two preload functions,
// which read like an attempted code-split but actually did nothing
// because the static imports in src/game/* already pulled pixi
// into the main chunk.  Vite warned about this on every build:
//   "pixi.js is dynamically imported by src/app.js but also
//    statically imported by [12 files], dynamic import will not
//    move module into another chunk."
// Using the static import explicitly is honest -- pixi IS in the
// main chunk on first paint by design.  See CONVERSION-PLAN Phase
// 4 "Pixi-deferred-load" for the future-work option of converting
// every src/game/* module to dynamic-load pixi inside its
// factories, which would shrink first-paint JS by ~140 KB gz.
import { Assets } from 'pixi.js';
import { loadVoiceSets, pickVoiceSetForLocale } from './audio/voice-set.js';
import { createGameAudio } from './audio/game-audio.js';
import {
  loadAppConfig,
  getHomeSelectAnimationMs,
  getHomeMascotConfig,
  getQuitButtonConfig,
  getQuizTuning,
} from './app-config.js';
import {
  unlock as unlockAudio,
  resume as resumeAudio,
  stopAll as stopAllAudio,
  preload as preloadAudio,
  play as playAudioOneShot,
  pause as pauseAudio,
  PlaybackToken,
} from './audio/engine.js';
import { createOrientationPrompt } from './ui/orientation-prompt.js';
import { createSplash } from './ui/splash.js';
import {
  start as startTelemetry,
  trackAppLoad,
  trackSceneStarted,
  trackRoundPlayed,
} from './telemetry.js';
import { createHomeScreen } from './ui/home.js';
import { createSettingsPanel } from './ui/settings-panel.js';
import { createQuitButton } from './ui/quit-button.js';
import {
  getActiveThemes, getLocale, getMaxCount, getNumeralScript,
  getPromptsEnabled, getCountingVoiceEnabled,
  getEntranceAnimations,
  ensureLocaleInitialized,
  onChange as onSettingsChange,
  SUPPORTED_LOCALES,
} from './settings.js';

import './styles/main.css';
import './styles/splash.css';
import './styles/orientation.css';
import './styles/home.css';
import './styles/quit-button.css';
import './styles/settings-panel.css';

/**
 * Initialize the app inside the given root element.
 *
 * @param {HTMLElement} root
 * @param {{
 *   skipSplash?: boolean,
 * }} [opts]
 */
export function initApp(root, opts = {}) {
  if (!(root instanceof HTMLElement)) {
    throw new TypeError('initApp: root must be an element');
  }

  root.innerHTML = '';
  root.classList.add('app-root');

  // --- First-boot locale detection ------------------------------------------
  // Resolves navigator.language to one of the supported locales and writes
  // it to storage ONCE on the very first boot.  Subsequent boots read the
  // stored value and skip detection so the user's explicit pick (made via
  // the settings panel) isn't clobbered by browser-language churn.  Pure
  // helper; safe to call before any UI renders.  See SETTINGS-SPEC §5.1.
  ensureLocaleInitialized();

  // --- Eager CJK webfont fetch ----------------------------------------------
  // PixiJS Text rasterises through Canvas2D at create time; if the
  // @font-face woff2 hasn't been fetched yet, the first plaque paints in
  // the fallback family with no DOM-style reflow to update it.  Probe
  // document.fonts.load() against every glyph the active script renders
  // BEFORE any plaque is built so the right font wins on first paint.
  // Fire-and-forget -- the probe resolves quickly even on slow networks
  // because the CJK subsets are <10 KB each, and arabic-script users
  // skip the probe entirely (no @font-face match for the latin range).
  // See src/numerals.js preloadScriptFont and src/styles/main.css
  // @font-face unicode-range gates.
  preloadScriptFont(getNumeralScript());

  // Re-preload when the user picks a different script in the settings
  // panel.  We don't track the prior value -- preloadScriptFont is
  // idempotent (the browser caches by font-face) and runs in tens of
  // milliseconds at most, so the redundant call when the change wasn't
  // a script change is negligible.
  onSettingsChange(() => {
    preloadScriptFont(getNumeralScript());
  });

  // --- PixiJS canvas (fills viewport, letterboxed) -------------------------
  const canvas = createCanvas(root);

  // Track the current scene and the parameters used to build it, so we can
  // rebuild with the same theme/count when the viewport crosses the aspect
  // ratio threshold.
  let currentScene = null;
  let currentThemeData = null;
  let currentThemeId = null;
  let currentObjectCount = 0;
  let rebuildInFlight = false;
  // True while an entrance animation is in flight.  We suppress
  // aspect-ratio-crossover rebuilds during entrance because restarting
  // the animation mid-flight is jarring and creates a gap where the
  // canvas is unlocked while async asset loads settle.  When the
  // entrance completes, handleLayoutChange is re-evaluated so any
  // pending variant switch takes effect then.
  let entranceInProgress = false;
  /** @type {PlaybackToken | null} */
  let currentPlaybackToken = null;
  /** @type {{ start(), stop(), destroy() } | null} */
  let currentIdleManager = null;
  /** @type {{ destroy() } | null} */
  let currentTapManager = null;
  /** @type {{ start(): void, destroy(): void } | null} */
  let currentCloudSpawner = null;
  /** @type {{ start(): void, destroy(): void } | null} */
  let currentBeeSpawner = null;
  /** @type {{ flash(durationMs?: number): Promise<void>, destroy(): void } | null} */
  let currentScreenFlash = null;
  // Per-theme thunder SFX URLs, shared by cloud-spawner instances
  // across every round that ships lightning-capable clouds.  The
  // three clips are cycled uniformly at random (matches the legacy
  // `thunder` sound playlist in AppConfiguration.plist).
  const THUNDER_URLS = [
    '/audio/sfx/thunder01.mp3',
    '/audio/sfx/thunder02.mp3',
    '/audio/sfx/thunder03.mp3',
  ];
  /** @type {'learn' | 'quiz' | null} */
  let currentGameMode = null;
  /**
   * Active learn-mode state machine.  Non-null only while a Learn session
   * is running; reset to null on teardown or when switching modes.  The
   * machine owns round cycling + audio sequencing; app.js just plumbs
   * the Round handles and handles teardown.
   * @type {{ start(): Promise<void>, stop(): Promise<void>, isRunning(): boolean } | null}
   */
  let currentLearnMachine = null;
  /**
   * Active quiz-mode state machine (Phase 2).  Symmetric with
   * currentLearnMachine -- only one of the two is non-null at a time.
   * @type {{ start(): Promise<void>, stop(): Promise<void>, isRunning(): boolean } | null}
   */
  let currentQuizMachine = null;
  /**
   * Active quiz plaque manager.  Non-null only during a quiz round
   * (between buildQuizRound's startRound callback and its teardown).
   * @type {{ destroy(): void } | null}
   */
  let currentQuizPlaqueManager = null;

  /**
   * Minimal Deferred — externally-resolvable Promise.  Used by the
   * state-machine bridge in buildLearnRound to expose whenEntranceDone
   * / whenComplete to the machine without entangling with internal
   * scene-build promise chains.
   * @returns {{ promise: Promise<void>, resolve: () => void }}
   */
  function deferred() {
    let resolve;
    const promise = new Promise((r) => { resolve = r; });
    return { promise, resolve };
  }

  // Dev-only theme focus: pin Learn mode to a single theme via the debug
  // dropdown.  In production, pickRandomTheme() uses the normal random
  // selection from the user's active themes list.
  //
  // The dropdown also exposes a sentinel "(random)" option -- when
  // selected, focusThemeId is set to null and pickRandomTheme() falls
  // back to its normal active-themes random pick.  The sentinel is
  // persisted in localStorage as FOCUS_RANDOM_SENTINEL so it survives
  // reloads.
  const FOCUS_THEME_IDS = ['sheep', 'chicks', 'cows', 'horses', 'flowers', 'apples'];
  const FOCUS_RANDOM_SENTINEL = '__random__';
  let focusThemeId = null;
  if (import.meta.env.DEV) {
    focusThemeId = 'apples';
    try {
      const saved = (typeof localStorage !== 'undefined')
        ? localStorage.getItem('focusTheme')
        : null;
      if (saved === FOCUS_RANDOM_SENTINEL) focusThemeId = null;
      else if (saved && FOCUS_THEME_IDS.includes(saved)) focusThemeId = saved;
    } catch { /* localStorage disabled/unavailable; keep default */ }
  }

  /**
   * Pick a random theme from the user's active themes list, filtered down
   * to themes whose entrance animations are implemented (present in
   * animations.json).
   *
   * Falls back to the unfiltered active list if the manifest can't be
   * loaded or no active theme is animated.
   *
   * @returns {Promise<string>} themeId
   */
  async function pickRandomTheme() {
    if (focusThemeId) return focusThemeId;
    const active = getActiveThemes();
    let animated;
    try {
      const manifest = await loadAnimationsData();
      animated = active.filter((id) => Object.prototype.hasOwnProperty.call(manifest, id));
    } catch {
      animated = [];
    }
    const pool = animated.length > 0 ? animated : active;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /**
   * Build (or rebuild) a scene with the given theme data and object count.
   * Locks the canvas design size to the chosen variant.
   *
   * @param {Object} themeData
   * @param {number} objectCount
   * @param {number[]} [positionIndices]  Reuse these position slots on rebuild.
   */
  /**
   * Build (or rebuild) the scene.
   *
   * @param {Object} themeData
   * @param {string} themeId
   * @param {number} objectCount
   * @param {number[]} [positionIndices]  Reuse these position slots.
   * @param {{
   *   skipEntrance?: boolean,
   *   learnRoundBridge?: {
   *     countModel: import('./game/count-model.js').CountModel,
   *     voiceSet: object,
   *     onEntranceDone: () => void,
   *     onRoundComplete?: () => void,
   *   },
   *   quizRoundBridge?: {
   *     voiceSet: object,
   *     gameAudio: object,
   *     maxCount: number,
   *     tuning?: {
   *       interPlaqueDelayMs?: number,
   *       plaqueEntrancePhase1Ms?: number,
   *       plaqueEntrancePhase2Ms?: number,
   *     },
   *     onEntranceDone: (plaqueManager: object) => void,
   *   },
   *   onSideGameTap?: () => void,
   *   onBeeTap?: () => void,
   *   onObjectTap?: () => void,
   * }} [opts]
   *   skipEntrance=true is used for variant swaps: the objects are
   *   placed at their final resting positions and no entrance
   *   animation plays.  When a new Learn round starts (skipEntrance=false,
   *   the default), the full entrance sequence runs.
   *
   *   learnRoundBridge is supplied by the Learn-mode state machine.  It
   *   threads the machine's pre-built countModel into the learn-tap
   *   manager (so the machine's onComplete callback fires), pins the
   *   voice set to the one the machine picked for this round, and
   *   signals whenEntranceDone via onEntranceDone() once managers are
   *   armed.
   */
  async function buildCurrentScene(themeData, themeId, objectCount, positionIndices, opts = {}) {
    const skipEntrance = !!opts.skipEntrance;
    const learnRoundBridge = opts.learnRoundBridge || null;
    const quizRoundBridge = opts.quizRoundBridge || null;
    const onSideGameTap = typeof opts.onSideGameTap === 'function'
      ? opts.onSideGameTap : null;
    const onBeeTap = typeof opts.onBeeTap === 'function'
      ? opts.onBeeTap : null;
    const onObjectTap = typeof opts.onObjectTap === 'function'
      ? opts.onObjectTap : null;
    // Treat the whole build-and-enter flow as one entrance-in-progress
    // window so handleLayoutChange doesn't try to rebuild concurrently
    // while we're loading assets or starting the entrance coroutine.
    // For skipEntrance swaps the flag is still set to prevent re-entry
    // but is cleared as soon as the new scene is mounted.
    entranceInProgress = true;

    // Cancel any in-flight entrance animation + audio from the previous scene.
    if (currentTapManager) {
      currentTapManager.destroy();
      currentTapManager = null;
    }
    if (currentIdleManager) {
      currentIdleManager.destroy();
      currentIdleManager = null;
    }
    if (currentCloudSpawner) {
      currentCloudSpawner.destroy();
      currentCloudSpawner = null;
    }
    if (currentBeeSpawner) {
      currentBeeSpawner.destroy();
      currentBeeSpawner = null;
    }
    if (currentScreenFlash) {
      currentScreenFlash.destroy();
      currentScreenFlash = null;
    }
    if (currentPlaybackToken) {
      currentPlaybackToken.cancel();
      currentPlaybackToken = null;
    }
    stopAllAudio();
    if (currentScene) {
      currentScene.destroy();
      currentScene = null;
    }
    await canvas.initPromise;
    currentScene = await buildScene(
      canvas.app.stage, themeData, objectCount, canvas, positionIndices,
    );
    currentThemeData = themeData;
    currentThemeId = themeId;
    currentObjectCount = objectCount;

    // Hide objects IMMEDIATELY (before any further async work) so
    // the PIXI rAF loop doesn't flash them at their final resting
    // positions while we await cloud-spawner mask/texture loads
    // below.  The entrance coroutine repositions + reveals each
    // sprite at the animation's start point.  For variant-swap
    // (skipEntrance) or "Entrance animations off" the flag is
    // false so sprites stay visible at their final positions -- the
    // pre-hide would otherwise cause a 1-tick flash between mount
    // and runEntrance's snap-in path.
    const willAnimate = !skipEntrance && getEntranceAnimations();
    if (willAnimate && currentScene?.objects) {
      for (const s of currentScene.objects) {
        s.visible = false;
      }
    }

    // Ambient cloud layer (Phase 2.5.1 / 2.5.2).  No-op for themes
    // that lack a cloudZoneRect / cloudImageNames in themes.json
    // (apples, horses, sheep).  Started right away so clouds are
    // visible through the entrance animation, not popping in
    // afterwards.  The lightning-flash overlay sits in its own
    // top-most container so the white flash occludes trees, barn,
    // and everything else when a cloud tap fires.
    currentScreenFlash = createScreenFlash({
      container: currentScene.lightningFlashContainer,
      designW: currentScene.designW,
      designH: currentScene.designH,
    });
    // Occluder URLs: every scene layer that visually covers clouds.
    // The backdrop always does; the optional overlay (cows, chicks,
    // horses) sits above objects and so also hides clouds behind
    // it.  Cloud-spawner samples each layer's alpha channel at the
    // tap's world point -- any opaque occluder rejects the hit.
    const variantCfg = themeData[currentScene.variant] || {};
    const occluderUrls = [];
    if (variantCfg.background) occluderUrls.push(`/${variantCfg.background}`);
    if (variantCfg.overlay) occluderUrls.push(`/${variantCfg.overlay}`);
    currentCloudSpawner = await createCloudSpawner({
      container: currentScene.cloudContainer,
      themeData,
      variant: currentScene.variant,
      designW: currentScene.designW,
      designH: currentScene.designH,
      occluderUrls,
      deps: {
        // Random thunder clip per tap.  playAudioOneShot returns a
        // Promise that resolves on playback end, which the spawner
        // uses to time bolt-sprite removal (chicks only).
        playThunder: () => {
          const url = THUNDER_URLS[Math.floor(Math.random() * THUNDER_URLS.length)];
          return playAudioOneShot(url);
        },
        triggerScreenFlash: () => {
          // Fire-and-forget; currentScreenFlash may have been
          // torn down while the tap was bubbling (rare).
          currentScreenFlash?.flash();
        },
        onSideGameTap: onSideGameTap || null,
      },
    });
    currentCloudSpawner.start();

    // Ambient bee fly-by (Phase 2.5.3a).  Enabled only on flowers --
    // other themes have no `flyingObject` block in themes.json and
    // the spawner short-circuits to a no-op.  Factory is SYNCHRONOUS
    // (preload happens in the background) so this call adds zero
    // additional microtask boundaries to the entrance-startup
    // timing budget the Game-mode tests' flush(30) is calibrated
    // against.
    //
    // Note: `start()` is deferred to AFTER the entrance animation
    // completes (below, alongside the idle manager's start), so the
    // bee doesn't enter the scene while the countable sprites are
    // still mid-entrance.
    currentBeeSpawner = createBeeSpawner({
      container: currentScene.beeContainer,
      themeData,
      variant: currentScene.variant,
      designW: currentScene.designW,
      designH: currentScene.designH,
      objects: currentScene.objects || [],
      deps: {
        // Every bee tap increments `bee_taps` telemetry (if the
        // round-scoped callback is wired).
        onBeeTap: onBeeTap || undefined,
        // Only the FIRST tap that starts the mini-game flips the
        // round's side_game_engaged flag.  Subsequent taps within
        // the same mini-game don't re-fire this.
        onMiniGameStart: onSideGameTap
          ? () => { try { onSideGameTap(); } catch { /* non-fatal */ } }
          : undefined,
      },
    });

    // Variant-swap path: the scene is already mounted with objects at
    // their final resting positions.  Leave them as-is (visible, on the
    // static sprite texture) and don't replay the entrance.
    if (skipEntrance) {
      entranceInProgress = false;
      // Start idle + blink for the swapped scene (assets should be
      // cached from the pre-swap build; this preload chain is fast).
      startIdleForCurrentScene(themeId);
      // With no entrance animation to wait on, the bee is clear to
      // start its spawn loop immediately.
      if (document.visibilityState === 'visible') {
        currentBeeSpawner?.start();
      }
      return;
    }

    // Normal entrance path.  Scene-load is phased:
    //   1. Preload entrance assets (cycle textures + scene sounds).
    //   2. Start runEntrance + kick off post-entrance preloads
    //      (idle textures, learn/quiz tap assets) IN PARALLEL.
    //   3. When runEntrance resolves, await the parallel preloads
    //      (they should already be done or very close) and build the
    //      idle + tap managers synchronously.
    //
    // This structure guarantees:
    //   - runEntrance never starts until its textures/sounds are
    //     ready, so cycles don't stall on first frame swap.
    //   - The visible entrance (1-3 s) overlaps the loading of idle
    //     + tap assets, so tap handlers are ready the instant
    //     entrance ends.  No "first tap silently dropped" gap.
    //   - Entrance and auxiliary preloads touch disjoint asset
    //     aliases (cycle keyframes vs idle/plaque/animator
    //     keyframes), so there's no PIXI Assets race.
    let animConfig = null;
    try {
      const animations = await loadAnimationsData();
      animConfig = animations[themeId];
    } catch (err) {
      console.warn('[app] skipped entrance (animations load failed):', err);
      entranceInProgress = false;
      // State machine must not hang awaiting whenEntranceDone when the
      // scene can't be animated.  Fire it so the machine can move on;
      // the subsequent whenComplete won't resolve (no manager to count)
      // but the machine's token-cancelled checks will catch a stop().
      if (learnRoundBridge) learnRoundBridge.onEntranceDone();
      if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
      return;
    }
    if (!animConfig || !currentScene) {
      entranceInProgress = false;
      if (learnRoundBridge) learnRoundBridge.onEntranceDone();
      if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
      return;
    }

    // --- Phase 1: preload entrance assets before starting animation ---
    try {
      await preloadCycleTextures(animConfig, currentScene.variant);
      const entranceSoundUrls = collectSceneSoundUrls(animConfig, currentScene.variant);
      if (entranceSoundUrls.length > 0) {
        await preloadAudio(entranceSoundUrls);
      }
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[app] entrance preload failed (runEntrance will retry):', err);
      }
      // Non-fatal: runEntrance will preload on its own as fallback.
    }

    // Scene may have been torn down during the preload await.
    if (!currentScene) {
      entranceInProgress = false;
      if (learnRoundBridge) learnRoundBridge.onEntranceDone();
      if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
      return;
    }

    // --- Phase 2: kick off post-entrance preloads in parallel ---
    // These load while the entrance animates.  Failures are
    // swallowed so a missing asset never blocks manager construction
    // -- the manager still builds with whatever loaded successfully.
    const idlePreloadPromise = preloadIdleTextures(animConfig, themeData).catch((err) => {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[app] idle preload failed:', err);
      }
      return {};
    });

    let learnAssetsPromise = null;
    if (currentGameMode === 'learn') {
      learnAssetsPromise = preloadLearnTapAssets(
        themeId, themeData, currentScene.objects.length,
        learnRoundBridge?.voiceSet || null,
      ).catch((err) => {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[app] learn-tap preload failed:', err);
        }
        return null;
      });
    }

    let quizAssetsPromise = null;
    if (currentGameMode === 'quiz') {
      const touchReactions = animConfig.touchReactions;
      quizAssetsPromise = (async () => {
        // Touch-reaction textures + sounds (for tapping countable objects
        // during the quiz round -- same idle-reaction flow as Learn mode).
        let tapTextureMap = null;
        if (touchReactions) {
          tapTextureMap = await preloadTouchReactionTextures(touchReactions);
          const { sounds } = collectTouchReactionAssets(touchReactions);
          if (sounds.length > 0) {
            await preloadAudio(sounds.map((s) => '/' + s));
          }
        }
        // Choice-plaque textures: always preload in quiz mode so the
        // state machine can hand the plaque manager a populated map.
        // Pass only THIS theme's data so we don't fetch every theme's
        // plaque pair on every round.
        const plaqueTextureMap = await preloadChoicePlaqueTextures({ [themeId]: themeData });
        // Capture the active numeral script so the createQuizPlaqueManager
        // call below renders 一 / いち / 일 instead of always defaulting
        // to arabic.  Mirrors what preloadLearnTapAssets does for the
        // learn-tap manager.  Without this the destructuring default
        // `numeralScript = 'arabic'` in createQuizPlaqueManager wins.
        const numeralScript = getNumeralScript();
        return { tapTextureMap, touchReactions, plaqueTextureMap, numeralScript };
      })().catch((err) => {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[app] quiz assets preload failed:', err);
        }
        return null;
      });
    }

    // --- Phase 2.5: install an early tap buffer ---
    // Sprites are already eventMode='static' from buildScene, but no
    // pointerdown listener is registered until the runEntrance.finally
    // block builds the tap manager.  That leaves a brief gap (entrance
    // visually finished -> finally awaits resolved -> manager built)
    // during which the user's first tap fires no handler and is lost.
    // We bridge the gap by wiring a tiny pointerdown handler per sprite
    // that just records the sprite into a pendingTaps Set; when the real
    // tap manager constructs, we pass the Set as pendingSprites so the
    // manager replays the tap.  The early handlers are removed AFTER
    // the real manager's listeners attach, to avoid a micro-window
    // where neither listener is active.
    const pendingTaps = (currentGameMode === 'learn') ? new Set() : null;
    const earlyTapHandlers = [];
    if (pendingTaps && currentScene?.objects) {
      for (let i = 0; i < currentScene.objects.length; i++) {
        const sprite = currentScene.objects[i];
        if (typeof sprite.on !== 'function') continue;
        const spriteIdx = i;
        const handler = () => {
          pendingTaps.add(sprite);
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log(`[app] early-tap buffered sprite ${spriteIdx}`
              + ` (pendingTaps.size=${pendingTaps.size},`
              + ` visible=${sprite.visible}, reacting=${sprite._reacting},`
              + ` counted=${sprite._counted})`);
          }
        };
        sprite.on('pointerdown', handler);
        earlyTapHandlers.push({ sprite, handler });
      }
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[app] early-tap buffer armed on ${earlyTapHandlers.length} sprites`);
      }
    }
    const removeEarlyTapHandlers = () => {
      if (import.meta.env.DEV && earlyTapHandlers.length > 0) {
        // eslint-disable-next-line no-console
        console.log(`[app] removing early-tap buffer`
          + ` (pendingTaps.size=${pendingTaps?.size ?? 0})`);
      }
      for (const { sprite, handler } of earlyTapHandlers) {
        if (typeof sprite.off === 'function') {
          try { sprite.off('pointerdown', handler); } catch { /* sprite torn down */ }
        }
      }
      earlyTapHandlers.length = 0;
    };

    // --- Phase 3: start the entrance animation ---
    // (Sprites were hidden up front, immediately after buildScene,
    // so the preload awaits above didn't expose their final
    // positions.)
    const token = new PlaybackToken();
    currentPlaybackToken = token;

    runEntrance({
      sprites: currentScene.objects,
      animConfig,
      variant: currentScene.variant,
      designW: currentScene.designW,
      designH: currentScene.designH,
      cancel: token,
      // Settings panel "Entrance animations" toggle.  When off, every
      // sprite snaps visible at its final resting position and the
      // staggered cycle-animation parade is skipped; music still plays.
      // Probed once at entry so a mid-round toggle doesn't produce a
      // half-animated scene -- the setting takes effect on the next
      // scene build.
      animationsEnabled: getEntranceAnimations,
    }).catch((err) => {
      console.warn('[app] entrance animation failed:', err);
    }).finally(async () => {
      // Only own cleanup/setup for the entrance that started with
      // this token.  If a new scene was built in the meantime, its
      // runEntrance manages its own token and finally().
      if (currentPlaybackToken !== token) {
        removeEarlyTapHandlers();
        if (learnRoundBridge) learnRoundBridge.onEntranceDone();
        if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
        return;
      }
      entranceInProgress = false;

      // Scene may have been torn down while entrance was in flight
      // (e.g. home button during animation).
      if (!currentScene) {
        removeEarlyTapHandlers();
        if (learnRoundBridge) learnRoundBridge.onEntranceDone();
        if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
        return;
      }

      try {
        // --- Phase 4: assemble managers from preloaded assets ---
        const idleTextureMap = await idlePreloadPromise;
        if (!currentScene) {
          removeEarlyTapHandlers();
          if (learnRoundBridge) learnRoundBridge.onEntranceDone();
          if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
          return;
        }

        const mgr = createIdleManager({
          sprites: currentScene.objects,
          animConfig,
          themeData,
          textureMap: idleTextureMap,
        });
        currentIdleManager = mgr;
        if (document.visibilityState === 'visible') {
          mgr.start();
        }

        // Entrance animation has finished -- ambient bee is clear
        // to start its spawn loop now.  (Flowers only; no-op on
        // every other theme.)
        if (document.visibilityState === 'visible') {
          currentBeeSpawner?.start();
        }

        if (currentGameMode === 'learn' && learnAssetsPromise) {
          const assets = await learnAssetsPromise;
          if (!currentScene || !assets) {
            removeEarlyTapHandlers();
            // Still signal whenEntranceDone so the state machine isn't
            // stranded awaiting; the subsequent whenComplete is unreachable
            // without a scene, so the machine will effectively stall --
            // but stalling is preferable to hanging the caller.
            if (learnRoundBridge) learnRoundBridge.onEntranceDone();
            if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
            return;
          }
          // Build the real manager FIRST (its constructor attaches
          // per-sprite pointerdown listeners AND synchronously replays
          // anything already in pendingTaps).  Only then remove the
          // early-tap buffer listeners, so a click that lands during
          // the handover always has SOMETHING listening.
          const replaySizeAtBuild = pendingTaps?.size ?? 0;
          currentTapManager = createLearnTapManager({
            sprites: currentScene.objects,
            theme: themeData,
            voiceSetsData: assets.voiceSetsData,
            locale: assets.locale,
            voiceSet: assets.voiceSet,
            numeralScript: assets.numeralScript,
            plaqueAnimations: assets.plaqueAnimations,
            plaqueTextureMap: assets.plaqueTextureMap,
            objectReactions: assets.objectReactions,
            reactionTextureMap: assets.reactionTextureMap,
            idleManager: mgr,
            pendingSprites: pendingTaps,
            countModel: learnRoundBridge?.countModel || null,
            onRoundComplete: learnRoundBridge?.onRoundComplete || null,
            // `verbalPromptsEnabled` here specifically gates the
            // number voice on tap.  Counting voice is distinct from
            // intros / praise: it plays in mode 'all' OR 'counting-
            // only', and silences only in 'off'.  That's what
            // getCountingVoiceEnabled returns.
            verbalPromptsEnabled: getCountingVoiceEnabled,
          });
          removeEarlyTapHandlers();
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log(`[app] learn-tap ready: ${currentScene.objects.length} sprites`
              + (replaySizeAtBuild ? ` (replayed ${replaySizeAtBuild} pending tap(s))` : ''));
          }
          // Signal the state machine that the round is armed and ready
          // for counting.  The machine will now play the COUNTING_PROMPT
          // audio, then await whenComplete (which fires when countModel's
          // onComplete callback resolves the deferred in buildLearnRound).
          if (learnRoundBridge) learnRoundBridge.onEntranceDone();
        } else if (currentGameMode === 'quiz' && quizAssetsPromise) {
          const assets = await quizAssetsPromise;
          if (!currentScene || !assets) return;
          // Countable-object tap manager (idle-style touch reactions).
          // Quiz mode still reacts to taps on the animals/flowers the
          // same way Learn mode does (spec §9: tap on a countable
          // object plays its touchReactionAnimation + resets the
          // assist timer, but doesn't count toward anything).
          if (assets.touchReactions && assets.tapTextureMap) {
            currentTapManager = createTapManager({
              sprites: currentScene.objects,
              touchReactions: assets.touchReactions,
              textureMap: assets.tapTextureMap,
              idleManager: mgr,
              // Round-scoped `object_taps` counter.  See the other
              // createTapManager call site below for the rationale.
              onTap: onObjectTap || undefined,
            });
          }
          // Build the choice-plaque manager and hand it to the quiz
          // state machine via the round bridge.  The machine will then
          // call addPlaques(correctValue) to spawn the plaques.
          if (quizRoundBridge && currentScene.choicePlaqueContainer) {
            // Translate the session-level app-config tuning into the
            // plaque manager's field names (interPlaqueDelayMs +
            // entrancePhase1Ms / entrancePhase2Ms + scales).  Missing
            // fields fall through to the manager's own defaults or
            // themes.json's per-theme values.
            const bridgeTuning = quizRoundBridge.tuning || {};
            currentQuizPlaqueManager = createQuizPlaqueManager({
              layer: currentScene.choicePlaqueContainer,
              theme: themeData,
              textureMap: assets.plaqueTextureMap,
              gameAudio: quizRoundBridge.gameAudio,
              designW: currentScene.designW,
              designH: currentScene.designH,
              maxCount: quizRoundBridge.maxCount,
              // The plaque manager defaults numeralScript to 'arabic'
              // in its destructuring; without this prop, picking
              // chinese / japanese / sino-korean / native-korean in
              // settings has no effect on the choice plaques.
              // fontFamily is intentionally omitted so the manager
              // derives the locale-correct stack from numeralScript
              // via getFontStackForScript().
              numeralScript: assets.numeralScript,
              tuning: {
                interPlaqueDelayMs: bridgeTuning.interPlaqueDelayMs,
                entrancePhase1Ms:   bridgeTuning.plaqueEntrancePhase1Ms,
                entrancePhase2Ms:   bridgeTuning.plaqueEntrancePhase2Ms,
                scales:             bridgeTuning.scales,
                fontSizeMultiplier: bridgeTuning.fontSizeMultiplier,
              },
            });
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[app] quiz round ready: plaque manager armed');
            }
            quizRoundBridge.onEntranceDone(currentQuizPlaqueManager);
          }
        }

        // A viewport aspect-ratio change may have been deferred
        // while the entrance was running.  Re-check now -- if the
        // variant now differs, this will do a skipEntrance swap.
        handleLayoutChange();
      } catch (err) {
        console.warn('[app] post-entrance setup failed:', err);
        removeEarlyTapHandlers();
        if (learnRoundBridge) learnRoundBridge.onEntranceDone();
        if (quizRoundBridge) quizRoundBridge.onEntranceDone(null);
      }
    });
  }

  /**
   * Start idle animations + blink overlays for the current scene,
   * plus wire up the tap manager (learn or quiz).
   *
   * Called ONLY by the skipEntrance (variant-swap) path now; the
   * normal entrance path assembles the same managers inline in
   * buildCurrentScene's runEntrance.finally() from pre-loaded
   * asset promises so they overlap with the animation.
   *
   * On a variant swap the assets were already loaded on the
   * pre-swap build, so every preload call here is a near-instant
   * cache hit.
   */
  async function startIdleForCurrentScene(themeId) {
    if (!currentScene) return;
    try {
      const animations = await loadAnimationsData();
      const animConfig = animations[themeId];
      if (!animConfig || !currentScene) return;
      const themeData = currentThemeData;
      const textureMap = await preloadIdleTextures(animConfig, themeData);
      // Guard: scene may have been destroyed while preloading
      if (!currentScene) return;
      const mgr = createIdleManager({
        sprites: currentScene.objects,
        animConfig,
        themeData,
        textureMap,
      });
      currentIdleManager = mgr;
      if (document.visibilityState === 'visible') {
        mgr.start();
      }

      // --- Tap manager wiring --------------------------------------------
      // In quiz mode, tapping a sprite plays a random reaction animation
      // (moo, tail wag, chirp, etc.).  In learn mode, taps trigger
      // counting voice playback and a themed number plaque entrance.
      if (currentGameMode === 'quiz') {
        const touchReactions = animConfig.touchReactions;
        if (touchReactions && currentScene) {
          const tapTextureMap = await preloadTouchReactionTextures(touchReactions);
          const { sounds } = collectTouchReactionAssets(touchReactions);
          if (sounds.length > 0) {
            await preloadAudio(sounds.map((s) => '/' + s));
          }
          if (!currentScene) return; // scene torn down while preloading
          currentTapManager = createTapManager({
            sprites: currentScene.objects,
            touchReactions,
            textureMap: tapTextureMap,
            idleManager: mgr,
            // Round-scoped `object_taps` telemetry counter; the
            // Quiz round handle exposes this via objectTaps() so
            // quiz-mode can fold it into round_played.
            onTap: onObjectTap || undefined,
          });
        }
        return;
      }

      if (currentGameMode === 'learn') {
        const assets = await preloadLearnTapAssets(
          themeId, themeData, currentScene.objects.length,
        );
        if (!currentScene || !assets) return;
        currentTapManager = createLearnTapManager({
          sprites: currentScene.objects,
          theme: themeData,
          voiceSetsData: assets.voiceSetsData,
          locale: assets.locale,
          voiceSet: assets.voiceSet,
          numeralScript: assets.numeralScript,
          plaqueAnimations: assets.plaqueAnimations,
          plaqueTextureMap: assets.plaqueTextureMap,
          objectReactions: assets.objectReactions,
          reactionTextureMap: assets.reactionTextureMap,
          idleManager: mgr,
          verbalPromptsEnabled: getCountingVoiceEnabled,
        });
      }
    } catch (err) {
      console.warn('[app] idle start failed:', err);
    }
  }

  /**
   * Preload every asset the Learn-tap manager will need for a scene:
   * voice-sets.json + picked voice set, static plaque texture,
   * entrance-animator keyframe textures, animator sounds, and the
   * number-recording audio for 1..maxObjects.
   *
   * Runs in parallel with the entrance animation (kicked off in
   * buildCurrentScene as soon as the scene is mounted) so the tap
   * manager can be built the instant entrance completes -- no dead
   * gap where the user's first tap is silently dropped.
   *
   * @param {string} themeId
   * @param {object} themeData
   * @param {number} maxObjects     Number of objects in the scene.
   * @param {object} [voiceSetOverride]
   *   If provided, this voice set is used instead of picking a fresh one.
   *   The Learn-mode state machine passes the round's pre-picked voice
   *   set here so the "Let's count the sheep!" prompt, number voice
   *   ("one", "two", ...), and success praise all sound like the same
   *   speaker within a round.
   * @returns {Promise<object>}     Opaque bag of preloaded assets.
   */
  async function preloadLearnTapAssets(themeId, themeData, maxObjects, voiceSetOverride = null) {
    // --- Voice set selection ---
    const voiceSetsData = await loadVoiceSets();
    const locale = getLocale();
    const voiceSet = voiceSetOverride || pickVoiceSetForLocale(voiceSetsData, locale);
    if (!voiceSet && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[app] no voice set for locale '${locale}' — learn-tap silent`);
    }
    const numeralScript = getNumeralScript();

    // --- Plaque static texture ---
    const plaqueTextureMap = await preloadPlaqueTextures({ [themeId]: themeData });

    // --- Animator keyframe textures + animator sounds ---
    const animationsRoot = await loadAnimationsData();
    const plaqueAnimationsRoot = animationsRoot?.plaqueAnimations || null;
    const objectReactionsRoot = animationsRoot?.objectReactions || null;
    const { images: animImages, sounds: animSounds } = collectPlaqueAnimatorAssets(
      { [themeId]: themeData },
      plaqueAnimationsRoot,
    );
    if (animImages.length > 0) {
      for (const p of animImages) {
        const alias = '/' + p;
        if (plaqueTextureMap.get && plaqueTextureMap.get(p)) continue;
        if (Assets.get(alias)) {
          plaqueTextureMap.set(p, Assets.get(alias));
          continue;
        }
        try { Assets.add({ alias, src: alias }); } catch { /* already registered */ }
        try {
          const tex = await Assets.load(alias);
          if (tex) plaqueTextureMap.set(p, tex);
        } catch (err) {
          if (import.meta.env?.DEV) {
            // eslint-disable-next-line no-console
            console.warn(`[app] plaque animator texture load failed: ${alias}`, err);
          }
        }
      }
    }

    // --- Plaque reaction (object-side, simultaneous) textures + sounds ---
    // The reactionTextureMap is a SEPARATE Map keyed by image path so
    // it doesn't collide with the plaque keyframe textures (which the
    // plaque sprite consumes), even though both come out of the same
    // pixi Assets cache.
    const reactionTextureMap = new Map();
    const { images: reactImages, sounds: reactSounds } = collectPlaqueReactionAssets(
      { [themeId]: themeData },
      objectReactionsRoot,
    );
    if (reactImages.length > 0) {
      for (const p of reactImages) {
        const alias = '/' + p;
        if (Assets.get(alias)) {
          reactionTextureMap.set(p, Assets.get(alias));
          continue;
        }
        try { Assets.add({ alias, src: alias }); } catch { /* already registered */ }
        try {
          const tex = await Assets.load(alias);
          if (tex) reactionTextureMap.set(p, tex);
        } catch (err) {
          if (import.meta.env?.DEV) {
            // eslint-disable-next-line no-console
            console.warn(`[app] plaque reaction texture load failed: ${alias}`, err);
          }
        }
      }
    }

    // --- Audio preloading: animator sounds + reaction sounds + number recordings 1..N ---
    const audioToPreload = [];
    for (const s of animSounds) audioToPreload.push('/' + s);
    for (const s of reactSounds) audioToPreload.push('/' + s);
    const voiceUrls = collectLearnVoiceUrls({
      voiceSetsData, locale, voiceSet, maxNumber: maxObjects, numeralScript,
    });
    audioToPreload.push(...voiceUrls);
    if (audioToPreload.length > 0) {
      await preloadAudio(audioToPreload);
    }

    return {
      voiceSetsData,
      locale,
      voiceSet,
      numeralScript,
      plaqueTextureMap,
      plaqueAnimations: plaqueAnimationsRoot,
      objectReactions: objectReactionsRoot,
      reactionTextureMap,
    };
  }

  /**
   * Start a scene in the given game mode.
   *
   *   - 'learn': handed off to createLearnModeMachine which drives
   *     SCENE_SETUP -> ENTRANCE -> COUNTING_PROMPT -> COUNTING ->
   *     LEARN_SUCCESS -> INTERLUDE and cycles to the next round on
   *     its own.  The machine is retained on currentLearnMachine so
   *     mode switches / restart buttons / app teardown can stop it.
   *   - 'quiz': the legacy single-scene path -- pick a random theme
   *     once and build it.  Quiz rounds don't cycle automatically
   *     (touch-reactions don't have a "done" signal in Phase 1d).
   *
   * @param {'learn' | 'quiz'} mode
   */
  // Serialization queue for startScene.  Overlapping startScene calls
  // (e.g. theme-picker change + Restart click in quick succession) used
  // to race: the first call would clear currentLearnMachine and await
  // its stop(), during which a second call would see the null global,
  // skip the stop, and create machine M2; when the first call resumed
  // it would create machine M3 and overwrite the reference to M2.  Both
  // machines then ran concurrently, issuing audio plays and mutating
  // the shared currentScene/currentPlaybackToken/currentTapManager
  // globals into chaos (music loops left playing, entrance sounds
  // dropped, taps routed to the wrong manager).  Serializing here
  // guarantees each startScene completes its full stop+start cycle
  // before the next one begins, so only one learn-mode machine exists
  // at a time.
  let sceneStartChain = Promise.resolve();
  // Serialize any operation that touches scene-level state
  // (currentLearnMachine, currentScene, audio, tap/idle managers).
  // Both startScene and returnToHome go through this so the quit
  // button can never interleave with an in-flight scene build.
  async function scheduleSceneChange(fn) {
    const prev = sceneStartChain;
    let releaseMyTurn;
    sceneStartChain = new Promise((r) => { releaseMyTurn = r; });
    try {
      try { await prev; } catch { /* earlier op's error already logged */ }
      await fn();
    } finally {
      releaseMyTurn();
    }
  }

  async function startScene(mode) {
    return scheduleSceneChange(() => doStartScene(mode));
  }

  async function doStartScene(mode) {
    // Stop any prior session cleanly.  Learn and quiz machines are
    // mutually exclusive but either might be live when the user
    // clicks a mode button; handling both keeps mode-switches safe.
    if (currentLearnMachine) {
      const m = currentLearnMachine;
      currentLearnMachine = null;
      await m.stop();
    }
    if (currentQuizMachine) {
      const m = currentQuizMachine;
      currentQuizMachine = null;
      await m.stop();
    }
    currentGameMode = mode;
    if (mode === 'learn') {
      await startLearnSession();
      return;
    }
    if (mode === 'quiz') {
      await startQuizSession();
      return;
    }
  }

  /**
   * Tear down the active scene and return the player to the home
   * screen.  Wired to the quit button (top-right red X).  Goes through
   * the same serialization queue as startScene so a quit click during
   * an in-flight scene build can't race with the build.
   */
  async function returnToHome() {
    return scheduleSceneChange(doReturnToHome);
  }

  async function doReturnToHome() {
    // Snapshot whichever state machine is active so we can stop it at
    // the end.  Null the globals first so any concurrent scene ops
    // don't see a stale reference.
    const learnM = currentLearnMachine;
    const quizM = currentQuizMachine;
    currentLearnMachine = null;
    currentQuizMachine = null;

    // Cancel any in-flight entrance animation + audio NOW.  This is
    // what makes the quit land instantly even if the child taps the
    // red X mid-entrance: runEntrance checks cancel.cancelled at every
    // step, aborts, and its .finally() resolves the round's
    // whenEntranceDone deferred.  That in turn unblocks the state
    // machine's `await whenEntranceDone`, which then observes its
    // own cancelled session token on the next boundary and exits.
    if (currentPlaybackToken) { currentPlaybackToken.cancel(); currentPlaybackToken = null; }
    stopAllAudio();

    // Destroy managers + scene synchronously so the canvas is visually
    // clean before we wait on the machine's teardown.  These are all
    // idempotent (null-guards), so it's fine if round.teardown() gets
    // called later in the machine's cleanup path.
    if (currentQuizPlaqueManager) {
      currentQuizPlaqueManager.destroy();
      currentQuizPlaqueManager = null;
    }
    if (currentTapManager) { currentTapManager.destroy(); currentTapManager = null; }
    if (currentIdleManager) { currentIdleManager.destroy(); currentIdleManager = null; }
    if (currentCloudSpawner) { currentCloudSpawner.destroy(); currentCloudSpawner = null; }
    if (currentBeeSpawner) { currentBeeSpawner.destroy(); currentBeeSpawner = null; }
    if (currentScreenFlash) { currentScreenFlash.destroy(); currentScreenFlash = null; }
    if (currentScene) { currentScene.destroy(); currentScene = null; }
    currentThemeData = null;
    currentThemeId = null;
    currentObjectCount = 0;
    currentGameMode = null;

    // Swap UI back to the home screen RIGHT NOW.  The child sees the
    // quit land instantly, while the machine's unwind ticks through
    // in the background.
    quitButton.hide();
    homeScreen.show();

    // Now that everything user-visible is torn down, let whichever
    // state machine was active finish unwinding.  Because we cancelled
    // the playback token above, runEntrance has already bailed (or
    // will within one frame), so the machine's in-flight awaits unblock
    // promptly and it exits.  Awaiting here keeps scheduleSceneChange's
    // serialization intact.
    const m = learnM || quizM;
    if (m) {
      try { await m.stop(); } catch { /* session error already logged */ }
    }
  }

  /**
   * Instantiate a Learn-mode state machine and kick off its session.
   * The machine owns round cycling; app.js only supplies round-builder
   * and teardown hooks.  Fire-and-forget — the returned run promise
   * resolves when the session exits (stop() or themePool exhausted).
   */
  async function startLearnSession() {
    const voiceSetsData = await loadVoiceSets();
    const locale = getLocale();
    // Note: scene_started fires per round (not per session) from
    // buildLearnRound / buildQuizRound, so every theme the pool
    // rotates through gets counted separately.  A session-level
    // firing here with `getActiveThemes()[0]` would mis-attribute
    // every round to whichever theme was first in the user's active
    // list regardless of what the pool actually picked.

    // Theme pool honours the DEV focus-theme override by pretending the
    // user has only that theme active.  In production, it mirrors the
    // user's active-theme setting so a change (e.g. Settings toggles)
    // is observed on the next pool rebuild.
    const themePool = createThemePool({
      getActiveThemes: () => {
        if (focusThemeId) return [focusThemeId];
        return getActiveThemes();
      },
    });

    const gameAudio = createGameAudio({
      voiceSetsData,
      locale,
      play: playAudioOneShot,
      pause: pauseAudio,
      // Probe the "Verbal prompts" mode on every call so the panel
      // can change it mid-session and the next round picks it up.
      // `getPromptsEnabled` is true ONLY in mode 'all' -- the counting-
      // only mode keeps number voice (via learn-tap below) but
      // silences intros / praise / comparator / assist prompts.
      verbalPromptsEnabled: getPromptsEnabled,
    });

    // In DEV, the count-override input is a *hard* override (matches
    // Quiz-mode behaviour): whatever the user types is the exact object
    // count for every round, not an upper bound.  Returns null when the
    // input is empty or <= 0 so the default random pick applies.
    const readDebugCount = () => {
      if (!import.meta.env.DEV || !debugCountInput) return null;
      const n = parseInt(debugCountInput.value, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const machine = createLearnModeMachine({
      themePool,
      getMaxCount,
      pickVoiceSet: () => pickVoiceSetForLocale(voiceSetsData, locale),
      gameAudio,
      startRound: buildLearnRound,
      createToken: () => new PlaybackToken(),
      pause: pauseAudio,
      trackRoundPlayed,

      // DEV count-override is read on every round so the user can type
      // a new value mid-session and see it take effect next round.
      // When the input is empty, the machine's default RNG picker runs
      // (uniform in [1, maxCount]).
      pickObjectCount: (maxCount) => {
        const override = readDebugCount();
        if (override !== null) return Math.min(override, 20);
        // Same default as learn-mode.js's defaultPickObjectCount.
        const clamped = Math.max(1, Math.min(20, Math.floor(maxCount || 1)));
        return Math.floor(Math.random() * clamped) + 1;
      },
      onState: import.meta.env.DEV
        ? (state, meta) => {
            // eslint-disable-next-line no-console
            console.log('[learn-mode] state:', state, meta || '');
          }
        : undefined,
    });

    currentLearnMachine = machine;
    // Fire-and-forget — the session runs until stop() or themePool
    // exhaustion.  Any unhandled error inside runSession is already
    // logged in learn-mode.js; we add a safety net for the outer promise.
    machine.start().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[app] learn-mode session failed:', err);
    });
  }

  /**
   * Round-builder callback invoked by the Learn-mode state machine.
   * Constructs the scene + entrance + managers, creates a countModel
   * bound to a whenComplete deferred, and returns a Round handle the
   * machine awaits.
   *
   * @param {{ themeId: string, objectCount: number, voiceSet: object, token: { cancelled: boolean } }} args
   * @returns {Promise<{
   *   countModel: import('./game/count-model.js').CountModel,
   *   whenEntranceDone: Promise<void>,
   *   whenComplete: Promise<void>,
   *   teardown: () => Promise<void>,
   * } | null>}
   */
  async function buildLearnRound({ themeId, objectCount, voiceSet /*, token */ }) {
    // Telemetry: one scene_started per round's theme.  The theme pool
    // rotates, so multi-theme sessions produce one event per theme
    // per round rather than one event per session (which would always
    // mis-attribute to the first active theme).
    try {
      trackSceneStarted({ mode: 'learn', theme: themeId, max_count: getMaxCount() });
    } catch { /* non-fatal */ }

    const entranceDef = deferred();
    const completeDef = deferred();

    // The count model is a plain counter -- its onComplete fires
    // synchronously the instant the count hits objectCount (the moment
    // the user taps the last sprite, before any voice/animation).  That
    // fires too early to gate the LEARN_SUCCESS praise: it would step
    // on the final number voice ("...three!").  Instead, the learn-tap
    // manager fires onRoundComplete AFTER the final tap's number audio
    // and entrance animation finish, and THAT resolves whenComplete.
    const countModel = createCountModel({ objectCount });

    let themeData;
    try {
      themeData = await loadThemeData(themeId);
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[app] loadThemeData failed for theme "${themeId}":`, err);
      }
      // Return null so the machine skips this round and draws again.
      return null;
    }

    // Round-scoped telemetry counters:
    //   sideGameEngaged - boolean, flipped true on any cloud tap
    //     OR the first bee tap that starts the mini-game.
    //   beeTaps         - integer, incremented on every bee tap
    //     (ambient + mini-game phases).  Flowers-only in practice;
    //     non-flowers themes produce a stable 0.
    // Both reset implicitly per round (fresh closure variables).
    // See METRICS-SPEC §4.4 for `side_game_engaged` + `bee_taps`.
    let sideGameEngaged = false;
    let beeTaps = 0;

    try {
      await buildCurrentScene(themeData, themeId, objectCount, undefined, {
        learnRoundBridge: {
          countModel,
          voiceSet,
          onEntranceDone: () => entranceDef.resolve(),
          onRoundComplete: () => completeDef.resolve(),
        },
        onSideGameTap: () => { sideGameEngaged = true; },
        onBeeTap: () => { beeTaps += 1; },
      });
    } catch (err) {
      // buildCurrentScene swallows most failures internally, but a
      // hard error from buildScene or the canvas init promise would
      // bubble up here.  Returning null tells the machine to skip
      // this round and draw again rather than crashing the session.
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[app] buildCurrentScene failed in learn round:', err);
      }
      countModel.destroy();
      entranceDef.resolve();
      completeDef.resolve();
      return null;
    }

    return {
      countModel,
      whenEntranceDone: entranceDef.promise,
      whenComplete: completeDef.promise,
      sideGameEngaged: () => sideGameEngaged,
      beeTaps: () => beeTaps,
      async teardown() {
        // Tear down this round's managers + scene.  The machine
        // calls this either after LEARN_SUCCESS + interjection (normal
        // path) or when the session is cancelled mid-round.  In the
        // cancelled case, the deferreds may still be unresolved; we
        // resolve them here so any lingering awaits inside the machine
        // (defensive) don't hang.
        if (currentTapManager) { currentTapManager.destroy(); currentTapManager = null; }
        if (currentIdleManager) { currentIdleManager.destroy(); currentIdleManager = null; }
    if (currentCloudSpawner) { currentCloudSpawner.destroy(); currentCloudSpawner = null; }
    if (currentBeeSpawner) { currentBeeSpawner.destroy(); currentBeeSpawner = null; }
    if (currentScreenFlash) { currentScreenFlash.destroy(); currentScreenFlash = null; }
        if (currentPlaybackToken) { currentPlaybackToken.cancel(); currentPlaybackToken = null; }
        stopAllAudio();
        if (currentScene) { currentScene.destroy(); currentScene = null; }
        countModel.destroy();
        entranceDef.resolve();
        completeDef.resolve();
      },
    };
  }

  /**
   * Instantiate a Quiz-mode state machine and kick off its session.
   * Symmetric with startLearnSession -- same theme pool + gameAudio
   * factory, different machine + round builder.
   *
   * Quiz timing tunings are read from app-config.json via getQuizTuning()
   * at session start and kept for the lifetime of the session: the
   * plaque-entrance phases, inter-plaque stagger, and post-answer
   * delay.  Changing the config values in app-config.json takes effect
   * on the next Learn/Quiz click, not mid-session.
   */
  async function startQuizSession() {
    const voiceSetsData = await loadVoiceSets();
    const locale = getLocale();

    const appCfg = await loadAppConfig();
    const quizTuning = getQuizTuning(appCfg);

    const themePool = createThemePool({
      getActiveThemes: () => {
        if (focusThemeId) return [focusThemeId];
        return getActiveThemes();
      },
    });

    const gameAudio = createGameAudio({
      voiceSetsData,
      locale,
      play: playAudioOneShot,
      pause: pauseAudio,
      // Probe the "Verbal prompts" mode on every call so the panel
      // can change it mid-session and the next round picks it up.
      // `getPromptsEnabled` is true ONLY in mode 'all' -- the counting-
      // only mode keeps number voice (via learn-tap below) but
      // silences intros / praise / comparator / assist prompts.
      verbalPromptsEnabled: getPromptsEnabled,
    });

    const readDebugCount = () => {
      if (!import.meta.env.DEV || !debugCountInput) return null;
      const n = parseInt(debugCountInput.value, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const machine = createQuizModeMachine({
      themePool,
      getMaxCount,
      pickVoiceSet: () => pickVoiceSetForLocale(voiceSetsData, locale),
      gameAudio,
      startRound: (ctx) => buildQuizRound(ctx, gameAudio, quizTuning),
      createToken: () => new PlaybackToken(),
      pause: pauseAudio,
      trackRoundPlayed,
      postAnswerDelay: quizTuning.postAnswerDelaySec,
      pickObjectCount: (maxCount) => {
        const override = readDebugCount();
        if (override !== null) return Math.min(override, 20);
        const clamped = Math.max(1, Math.min(20, Math.floor(maxCount || 1)));
        return Math.floor(Math.random() * clamped) + 1;
      },
      onState: import.meta.env.DEV
        ? (state, meta) => {
            // eslint-disable-next-line no-console
            console.log('[quiz-mode] state:', state, meta || '');
          }
        : undefined,
    });

    currentQuizMachine = machine;
    machine.start().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[app] quiz-mode session failed:', err);
    });
  }

  /**
   * Round-builder callback invoked by the Quiz-mode state machine.
   * Mirrors buildLearnRound but returns a QuizRound shape:
   * { plaqueManager, whenEntranceDone, teardown }.  The plaque manager
   * is built by buildCurrentScene after the entrance animation finishes
   * and handed back via the quizRoundBridge's onEntranceDone callback.
   *
   * @param {{ themeId: string, objectCount: number, voiceSet: object, token: object }} ctx
   * @param {object} gameAudio  The createGameAudio()-built helpers for
   *                            the session (pop sounds + comparator + etc.).
   * @returns {Promise<{
   *   plaqueManager: object,
   *   whenEntranceDone: Promise<void>,
   *   teardown: () => Promise<void>,
   * } | null>}
   */
  async function buildQuizRound(
    { themeId, objectCount, voiceSet /*, token */ },
    gameAudio,
    quizTuning,
  ) {
    // Telemetry: one scene_started per round's theme.  See the
    // matching comment in buildLearnRound for the rationale.
    try {
      trackSceneStarted({ mode: 'quiz', theme: themeId, max_count: getMaxCount() });
    } catch { /* non-fatal */ }

    const entranceDef = deferred();
    /** @type {object | null} */
    let plaqueManager = null;

    let themeData;
    try {
      themeData = await loadThemeData(themeId);
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[app] loadThemeData failed for theme "${themeId}":`, err);
      }
      return null;
    }

    if (!themeData?.quizPlaque) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[app] theme "${themeId}" has no quizPlaque config; skipping round`);
      }
      return null;
    }

    // Round-scoped telemetry counters; see buildLearnRound for
    // rationale and METRICS-SPEC §4.4 for the field definitions.
    // object_taps is Quiz-specific here -- Learn derives it from
    // the countModel's count, but in Quiz there's no countModel so
    // we count taps on countable sprites directly via the
    // tap-manager's onTap hook.
    let sideGameEngaged = false;
    let beeTaps = 0;
    let objectTaps = 0;

    try {
      // maxCount bounds the wrong-value pool in the plaque manager.
      // Normally getMaxCount() (the user's settings value, default 10)
      // is enough -- it IS the range objectCount was drawn from.  But
      // DEV tooling (the debug count-override input) can force
      // objectCount past the setting, which would leave the pool
      // stuck at ≤ getMaxCount and every wrong plaque ≤ 10 even
      // though the correct plaque is e.g. 15.  Taking max(setting,
      // objectCount) keeps wrong values plausible relative to the
      // correct value in that DEV case, and is a no-op in normal
      // play where objectCount ≤ getMaxCount() by construction.
      const poolMaxCount = Math.max(getMaxCount(), objectCount);
      await buildCurrentScene(themeData, themeId, objectCount, undefined, {
        quizRoundBridge: {
          voiceSet,
          gameAudio,
          maxCount: poolMaxCount,
          tuning: quizTuning,
          onEntranceDone: (mgr) => {
            plaqueManager = mgr;
            entranceDef.resolve();
          },
        },
        onSideGameTap: () => { sideGameEngaged = true; },
        onBeeTap: () => { beeTaps += 1; },
        onObjectTap: () => { objectTaps += 1; },
      });
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[app] buildCurrentScene failed in quiz round:', err);
      }
      entranceDef.resolve();
      return null;
    }

    return {
      // plaqueManager may still be null when the bridge fires with a
      // teardown-before-entrance-complete state.  The state machine
      // inspects the whenEntranceDone then reads plaqueManager via
      // the proxy object below; if null, it treats the round as
      // failed and skips.
      get plaqueManager() {
        return plaqueManager || makeNoopPlaqueManager();
      },
      whenEntranceDone: entranceDef.promise,
      sideGameEngaged: () => sideGameEngaged,
      beeTaps: () => beeTaps,
      objectTaps: () => objectTaps,
      async teardown() {
        if (currentQuizPlaqueManager) {
          currentQuizPlaqueManager.destroy();
          currentQuizPlaqueManager = null;
        }
        if (currentTapManager) { currentTapManager.destroy(); currentTapManager = null; }
        if (currentIdleManager) { currentIdleManager.destroy(); currentIdleManager = null; }
    if (currentCloudSpawner) { currentCloudSpawner.destroy(); currentCloudSpawner = null; }
    if (currentBeeSpawner) { currentBeeSpawner.destroy(); currentBeeSpawner = null; }
    if (currentScreenFlash) { currentScreenFlash.destroy(); currentScreenFlash = null; }
        if (currentPlaybackToken) { currentPlaybackToken.cancel(); currentPlaybackToken = null; }
        stopAllAudio();
        if (currentScene) { currentScene.destroy(); currentScene = null; }
        entranceDef.resolve();
      },
    };
  }

  /**
   * Fallback plaque-manager stub returned when the real manager
   * couldn't be built (scene failed to assemble, missing textures,
   * etc.).  Every method is a no-op / never-resolving promise, which
   * the state machine cancels on its next token check.
   */
  function makeNoopPlaqueManager() {
    return {
      async addPlaques() { /* no-op */ },
      onTap() { return () => {}; },
      async playCorrectAnimation() { /* no-op */ },
      async playIncorrectAnimation() { /* no-op */ },
      async dimAllUnselected() { /* no-op */ },
    };
  }

  // --- Aspect-ratio crossover watcher ---------------------------------------
  // When the viewport crosses the standard/wide threshold while the scene
  // is IDLE (entrance animation complete, objects at rest), rebuild with
  // the new variant's backgrounds + object positions.  This is a pure
  // "swap variant" operation -- no entrance animation replays, the
  // objects simply appear at their corresponding positions in the new
  // variant's positions array.
  //
  // While an entrance animation is in flight, we DEFER the swap.
  // Rebuilding mid-animation would be jarring and also opens a race
  // window where canvas.resize runs without a design-size lock during
  // the async asset load.  When the entrance finishes, its finally()
  // re-runs handleLayoutChange so any deferred swap happens then.
  function handleLayoutChange() {
    if (!currentScene || rebuildInFlight) return;
    if (entranceInProgress) return;   // re-checked when entrance completes
    // While the Learn-mode state machine is driving the session, don't
    // attempt a mid-round variant swap.  A swap tears down the
    // learn-tap manager, which would orphan the machine's countModel
    // hook (the new manager would never call increment with the
    // machine's expectation).  The legacy iOS app was locked to
    // landscape so this case never arose; for the web we simply leave
    // the in-progress round in its original variant and pick up the
    // new variant on the NEXT round build.
    if (currentLearnMachine) return;
    if (currentQuizMachine) return;

    const idealVariant = getLayoutVariant();
    if (idealVariant !== currentScene.variant) {
      rebuildInFlight = true;
      const prevIndices = currentScene.selectedIndices;
      buildCurrentScene(
        currentThemeData, currentThemeId, currentObjectCount, prevIndices,
        { skipEntrance: true },
      ).finally(() => { rebuildInFlight = false; });
    }
  }
  window.addEventListener('resize', handleLayoutChange);

  // --- Dev-only debug controls -----------------------------------------------
  // Restart button, theme picker dropdown, object count override, and
  // variant label.  All guarded behind import.meta.env.DEV so Vite
  // tree-shakes them from the production bundle.
  let debugRestartBtn = null;
  let debugThemeSelect = null;
  let debugCountInput = null;
  let debugVariantLabel = null;
  let updateVariantLabel = null;

  if (import.meta.env.DEV) {
    // --- Restart button ---
    debugRestartBtn = document.createElement('button');
    debugRestartBtn.type = 'button';
    debugRestartBtn.textContent = 'Restart';
    debugRestartBtn.setAttribute('aria-label', 'Restart current scene (debug)');
    Object.assign(debugRestartBtn.style, {
      position: 'fixed',
      top: '0.5rem',
      left: '0.5rem',
      zIndex: '9999',
      padding: '0.35rem 0.75rem',
      fontSize: '0.8rem',
      fontFamily: 'monospace',
      background: 'rgba(0, 0, 0, 0.6)',
      color: '#fff',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      cursor: 'pointer',
    });
    debugRestartBtn.addEventListener('click', () => {
      // Restart is in-scene-only.  When the home screen is up
      // (currentGameMode === null), there's nothing to restart -- the
      // child still has to pick Learn or Quiz.  Earlier behaviour of
      // auto-starting a learn session from here was confusing because
      // it bypassed the home-screen prompt + button animation.
      const mode = currentGameMode;
      if (!mode) return;
      // Reusing startScene routes through scheduleSceneChange so the
      // currently-active learn machine (if any) is stopped, currentScene
      // is torn down, and a fresh theme + entrance run from scratch.
      // For learn mode, this stops the state machine cleanly; for quiz
      // mode, pickRandomTheme() runs again (honouring focusThemeId) and
      // a brand-new scene + entrance is built.  No special-casing per
      // mode -- "restart" === "stop everything, start scene selection
      // fresh".
      startScene(mode);
    });
    root.appendChild(debugRestartBtn);

    // --- Theme picker dropdown ---
    // Setting this dropdown is a *passive* override: it only mutates
    // focusThemeId (which pickRandomTheme reads).  It does NOT start a
    // scene -- if the child is on the home screen, the next Learn/Quiz
    // tap will pick up the new focus; if a scene is already running,
    // the child can hit Restart to apply the new focus immediately.
    // The "(random)" sentinel clears the focus so pickRandomTheme falls
    // back to its normal random pick from the active themes list.
    debugThemeSelect = document.createElement('select');
    debugThemeSelect.setAttribute('aria-label', 'Focus theme (debug)');
    Object.assign(debugThemeSelect.style, {
      position: 'fixed',
      top: '0.5rem',
      left: '5rem',
      zIndex: '9999',
      padding: '0.3rem 0.5rem',
      fontSize: '0.8rem',
      fontFamily: 'monospace',
      background: 'rgba(0, 0, 0, 0.6)',
      color: '#fff',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      cursor: 'pointer',
    });
    // Sentinel "(random)" option first so it's the obvious top choice.
    {
      const opt = document.createElement('option');
      opt.value = FOCUS_RANDOM_SENTINEL;
      opt.textContent = '(random)';
      if (focusThemeId === null) opt.selected = true;
      debugThemeSelect.appendChild(opt);
    }
    for (const id of FOCUS_THEME_IDS) {
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = id;
      if (id === focusThemeId) opt.selected = true;
      debugThemeSelect.appendChild(opt);
    }
    debugThemeSelect.addEventListener('change', () => {
      const value = debugThemeSelect.value;
      if (value === FOCUS_RANDOM_SENTINEL) {
        focusThemeId = null;
      } else {
        focusThemeId = value;
      }
      try { localStorage.setItem('focusTheme', value); } catch { /* no-op */ }
      // No scene start here -- the override is purely a hint that the
      // randomizer (pickRandomTheme) consults the next time it runs.
      // To apply the change to a currently-running scene, hit Restart.
    });
    root.appendChild(debugThemeSelect);

    // --- Object count override ---
    debugCountInput = document.createElement('input');
    debugCountInput.type = 'number';
    debugCountInput.min = '0';
    debugCountInput.max = '21';
    debugCountInput.placeholder = '#';
    debugCountInput.setAttribute('aria-label', 'Object count override (debug)');
    Object.assign(debugCountInput.style, {
      position: 'fixed',
      top: '0.5rem',
      left: '13rem',
      zIndex: '9999',
      width: '3.2rem',
      padding: '0.3rem 0.4rem',
      fontSize: '0.8rem',
      fontFamily: 'monospace',
      background: 'rgba(0, 0, 0, 0.6)',
      color: '#fff',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      textAlign: 'center',
    });
    root.appendChild(debugCountInput);

    // --- Variant indicator label ---
    debugVariantLabel = document.createElement('div');
    debugVariantLabel.setAttribute('aria-hidden', 'true');
    Object.assign(debugVariantLabel.style, {
      position: 'fixed',
      bottom: '0.5rem',
      left: '0.5rem',
      zIndex: '9999',
      padding: '0.25rem 0.6rem',
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      background: 'rgba(0, 0, 0, 0.6)',
      color: '#fff',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      pointerEvents: 'none',
    });
    updateVariantLabel = () => {
      debugVariantLabel.textContent = getLayoutVariant();
    };
    updateVariantLabel();
    window.addEventListener('resize', updateVariantLabel);
    root.appendChild(debugVariantLabel);
  }

  // --- Orientation prompt (JS-driven, detects mobile vs desktop) -----------
  const orientationPrompt = createOrientationPrompt(root);

  // --- Home screen (overlays the canvas until the child starts a game) -----
  // playSelectPrompt plays the mode-specific session-start prompt
  // ("Let's learn to count!" / "Let's practice counting!") on the home
  // screen *before* the scene starts, so the player gets verbal feedback
  // confirming their choice while the buttons animate.  Because the
  // prompt fires here, the Learn-mode state machine no longer plays
  // letslearntocount itself -- the home screen owns that prompt now.
  async function playHomeSelectPrompt(mode) {
    const voiceSetsData = await loadVoiceSets();
    const locale = getLocale();
    const voiceSet = pickVoiceSetForLocale(voiceSetsData, locale);
    if (!voiceSet) return;
    const ga = createGameAudio({
      voiceSetsData,
      locale,
      play: playAudioOneShot,
      pause: pauseAudio,
      verbalPromptsEnabled: getPromptsEnabled,
    });
    if (mode === 'learn') await ga.playLearnSessionStart(voiceSet);
    else await ga.playQuizSessionStart(voiceSet);
  }

  // --- Settings panel (SETTINGS-SPEC slice e.2 + e.3) ---------------------
  // Instantiated up front so the home screen can bind to it.  Mounted
  // into root, not document.body, so teardown via the home-screen /
  // initApp destroy path is straightforward.  The panel is dormant
  // until the home-screen Settings button's three-tap gate clears and
  // calls onSettings(), which toggles the panel below.
  //
  // availableLocales: pass every SUPPORTED_LOCALES entry now that Phase 3
  // has wired up the audio (3a), numeral scripts (3b), and locale state
  // (3c) for en / zh-CN / ja / ko.  The panel still falls back to a
  // single-locale disabled dropdown if a future build wants to gate
  // language switching, but the default for an in-tree build is "all
  // locales selectable" so users can swap freely.
  // settingsPanel + homeScreen are wrapped in `let` and rebuilt on
  // locale change (slice 3d).  The settings panel + home screen capture
  // their UI-string locale at construction time -- swapping it requires
  // a destroy + rebuild.  Mascot config (loaded async from
  // app-config.json) is replayed on rebuild via a captured ref so a
  // language change doesn't reset the mascot back to defaults.
  let settingsPanel;
  let homeScreen;
  let lastMascotConfig = null;
  let lastSelectAnimationMs = null;

  function handleLearn() {
    homeScreen.hide();
    quitButton.show();
    startScene('learn');
  }
  function handleQuiz() {
    homeScreen.hide();
    quitButton.show();
    startScene('quiz');
  }
  function handleSettingsToggle() {
    // Called when the gate clears OR when the button is tapped while
    // the panel is already open.  In both cases we want a toggle:
    // open from closed, close from open.
    if (settingsPanel.isOpen()) settingsPanel.close();
    else settingsPanel.open();
  }

  function buildLocalizedUI(localeForUi) {
    settingsPanel = createSettingsPanel({
      locale: localeForUi,
      availableLocales: SUPPORTED_LOCALES,
    });
    root.appendChild(settingsPanel.panelElement);

    homeScreen = createHomeScreen(root, {
      locale: localeForUi,
      playSelectPrompt: playHomeSelectPrompt,
      // Mascot baa: fed the sfx URL at t=150 ms inside the jump reaction.
      // Uses the shared engine.play() so the Howler AudioContext the
      // mascot awakens is the same one the scene will reuse.
      playSheepBaa: (src) => playAudioOneShot(src),
      onLearn: handleLearn,
      onQuiz: handleQuiz,
      onSettings: handleSettingsToggle,
      isSettingsOpen: () => settingsPanel.isOpen(),
    });
    // Replay any app-config tunables we already received (mascot,
    // select-animation-ms) so a rebuild after locale change doesn't
    // revert them to defaults.
    if (lastSelectAnimationMs !== null) {
      homeScreen.setSelectAnimationMs(lastSelectAnimationMs);
    }
    if (lastMascotConfig !== null) {
      homeScreen.setMascotConfig(lastMascotConfig);
    }
  }

  buildLocalizedUI(getLocale());

  // Re-render the panel + home screen when the user picks a different
  // language so the labels switch without a page reload.  Other settings
  // changes (max count, themes, etc.) don't need a rebuild -- those
  // controls update themselves in place.
  let lastLocale = getLocale();
  onSettingsChange((state) => {
    const nextLocale = state && state.locale;
    if (!nextLocale || nextLocale === lastLocale) return;
    lastLocale = nextLocale;
    // Tear down old DOM in place, then rebuild.  Order matters: panel
    // first (so the home screen doesn't briefly show a click target
    // bound to a destroyed panel), then home.  show()/hide() state is
    // implicitly fresh because rebuild constructs a visible home and a
    // closed panel -- if a Learn/Quiz scene was running we leave the
    // home hidden via the conditional below.
    //
    // Capture the panel-open state BEFORE destroy so we can restore it
    // after the rebuild.  Locale is changed from inside the panel
    // (Language section), so the panel is essentially always open at
    // this point -- but capturing the bit makes the rebuild correct
    // even if a future caller flips locale programmatically with the
    // panel closed.  Restoring the open state lets the user pick a
    // language and then immediately pick a numeral script in the
    // same Settings session, instead of having to re-open the panel.
    const wasHomeHidden = homeScreen.element.style.display === 'none';
    const wasSettingsOpen = settingsPanel.isOpen();
    settingsPanel.destroy();
    homeScreen.destroy();
    buildLocalizedUI(nextLocale);
    if (wasHomeHidden) homeScreen.hide();
    if (wasSettingsOpen) settingsPanel.open();
  });

  // Red-X quit button -- top-right overlay on every Learn/Quiz scene.
  // Hidden while the home screen is up (initial + post-quit) and
  // shown when a scene starts.  Clicking it tears down the current
  // session and brings the home screen back.  The button is appended
  // to `root` so it inherits .app-root's positioning context.
  //
  // We thread canvas.getLayoutVariant in as the variant picker so the
  // button uses exactly the same standard/wide crossover as the rest
  // of the app.  Both variants are styled from app-config.json
  // (imageUrl, size, centre point); the module re-applies the active
  // variant on every window resize.
  const quitButton = createQuitButton(root, {
    onQuit: () => { returnToHome(); },
    getVariant: getLayoutVariant,
  });
  // Apply app-wide UI tuning from public/app-config.json once it loads.
  // The home screen is fully functional with its built-in default while
  // this is in flight; the fetch is local and will resolve well before
  // any user interaction.  The quit button gets a config object with
  // standard + wide entries so it can swap styling on aspect-ratio
  // crossover without further round-trips.
  loadAppConfig().then((cfg) => {
    const mascotCfg = getHomeMascotConfig(cfg);
    lastSelectAnimationMs = getHomeSelectAnimationMs(cfg);
    lastMascotConfig = mascotCfg;
    homeScreen.setSelectAnimationMs(lastSelectAnimationMs);
    homeScreen.setMascotConfig(mascotCfg);
    quitButton.setConfig({
      standard: getQuitButtonConfig(cfg, 'standard'),
      wide: getQuitButtonConfig(cfg, 'wide'),
    });
    // Dev-only diagnostic: print the applied mascot config so tuning
    // values in public/app-config.json can be verified in DevTools
    // without wiring up a custom observer.  Double-gated, mirroring
    // audio/engine.js's pattern: stripped from prod via
    // import.meta.env.DEV, and muted by default in dev unless the
    // caller sets `localStorage.appDebug = '1'` in the browser console.
    if (import.meta.env?.DEV) {
      let appDebug = false;
      try {
        appDebug = typeof localStorage !== 'undefined'
          && localStorage.getItem('appDebug') === '1';
      } catch { /* no-op */ }
      if (appDebug) {
        // eslint-disable-next-line no-console
        console.debug('[app] mascot config applied', mascotCfg);
      }
    }
  });

  // --- Telemetry boot ------------------------------------------------------
  // Fire-and-forget config fetch; the client queues events during the
  // fetch window and dispatches them once lifecycle becomes 'active'.
  // Any fetch failure fail-closes the client for the rest of the session
  // (docs/METRICS-SPEC.md §6.4), so a missing /api/telemetry backend in
  // a dev build doesn't leak queued events.
  startTelemetry();

  // Helper for the app_load event -- called from splash onDismiss (if
  // splash shown) or immediately below (if skipSplash).
  function _emitAppLoad() {
    try {
      const standalone = (typeof navigator !== 'undefined' && navigator.standalone === true)
        || (typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia('(display-mode: standalone)').matches);
      trackAppLoad({ pwa_installed: !!standalone });
    } catch { /* non-fatal */ }
  }

  // --- Splash overlay (captures first gesture for audio unlock) ------------
  let splash = null;
  if (!opts.skipSplash) {
    splash = createSplash(root, {
      onDismiss: () => {
        // Audio unlock MUST run on the user-gesture stack so iOS Safari
        // allows subsequent Web Audio playback.
        unlockAudio();
        // Fire the app_load event now that the splash has been
        // dismissed and the first interactive frame is live.
        _emitAppLoad();
      },
    });
  } else {
    // No splash (test harness / --skipSplash opt) -- still emit app_load
    // so the initApp-on-boot path isn't silent for telemetry.
    _emitAppLoad();
  }

  // --- Visibility change ---------------------------------------------------
  // When the child switches away (e.g. home button on iOS), pause any
  // in-progress game activity. Phase 1c+ will add sound/animation stops.
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // Safari pauses rAF/timers aggressively for hidden documents.
      // If that happens mid-entrance, cancelling leaves the round in a
      // half-entered state.  Fast-forward the current entrance to its
      // settled pose instead so the scene comes back ready to play.
      if (currentIdleManager) currentIdleManager.stop();
      if (entranceInProgress && currentPlaybackToken) {
        settleEntranceSprites(currentScene?.objects || []);
        currentPlaybackToken.fastForward();
      }
      stopAllAudio();
    } else if (document.visibilityState === 'visible') {
      // Safari (and some other browsers) suspend the AudioContext when
      // the page loses visibility (screen lock, tab switch).  The
      // context stays suspended even after the page is foregrounded
      // again, which silences all subsequent playback.  Calling resume()
      // here re-activates the context so sounds work again.
      resumeAudio();
      // Restart idle animations + blink that were paused when hidden.
      if (currentIdleManager) currentIdleManager.start();
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    canvas,
    homeScreen,
    quitButton,
    splash,
    destroy() {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleLayoutChange);
      if (currentLearnMachine) {
        const m = currentLearnMachine;
        currentLearnMachine = null;
        // Fire-and-forget — the machine will cancel its token and
        // tear down the current round; destroy() can't await it
        // because it's a sync API.
        m.stop();
      }
      if (currentQuizMachine) {
        const m = currentQuizMachine;
        currentQuizMachine = null;
        m.stop();
      }
      if (currentQuizPlaqueManager) {
        currentQuizPlaqueManager.destroy();
        currentQuizPlaqueManager = null;
      }
      if (currentTapManager) { currentTapManager.destroy(); currentTapManager = null; }
      if (currentIdleManager) { currentIdleManager.destroy(); currentIdleManager = null; }
    if (currentCloudSpawner) { currentCloudSpawner.destroy(); currentCloudSpawner = null; }
    if (currentBeeSpawner) { currentBeeSpawner.destroy(); currentBeeSpawner = null; }
    if (currentScreenFlash) { currentScreenFlash.destroy(); currentScreenFlash = null; }
      if (currentPlaybackToken) currentPlaybackToken.cancel();
      stopAllAudio();
      quitButton.destroy();
      settingsPanel.destroy();
      orientationPrompt.destroy();
      // Dev-only debug controls cleanup.
      if (debugRestartBtn?.parentNode) debugRestartBtn.parentNode.removeChild(debugRestartBtn);
      if (debugThemeSelect?.parentNode) debugThemeSelect.parentNode.removeChild(debugThemeSelect);
      if (debugCountInput?.parentNode) debugCountInput.parentNode.removeChild(debugCountInput);
      if (updateVariantLabel) window.removeEventListener('resize', updateVariantLabel);
      if (debugVariantLabel?.parentNode) debugVariantLabel.parentNode.removeChild(debugVariantLabel);
      if (currentScene) {
        currentScene.destroy();
        currentScene = null;
      }
      canvas.destroy();
      root.innerHTML = '';
      root.classList.remove('app-root');
    },
  };
}
